import type { Member, User, ViewFilters } from "../../domain/app-types.ts";
import type { Category, Todo } from "../../domain/entities.ts";
import { collectionInsights } from "./collection-insights.ts";
import { collectionInsightsView } from "./collection-insights-view.ts";
import { overviewLabel } from "../navigation/view-orientation.ts";

export interface CollectionOverviewCapabilities {
  document: Document;
  collectionId(): string | null;
  categories(): Category[];
  tasks(): Todo[];
  members(): Member[];
  user(): User | null;
  today(): string;
  collectionName(id: string): string;
  rootName(): string;
  defaultFilters(): ViewFilters;
  activate(filters: ViewFilters): void;
  render(): Promise<void>;
  closeIcon: string;
  now(): number;
}

export function collectionOverviewFilters(defaults: ViewFilters, collectionId: string | null, segment: string, assigneeId = "", wipTaskIds: string[] = []): ViewFilters {
  const filters = { ...defaults, categoryId: collectionId || "" };
  if (segment === "completed" || segment === "recent") filters.statuses = ["completed"];
  if (segment === "overdue") filters.due = "overdue";
  if (segment === "blocked") filters.blocked = true;
  if (segment === "inactive") filters.inactiveDays = 30;
  if (segment === "recent") filters.completedWithin = "week";
  if (segment === "estimated") filters.estimatedOnly = true;
  if (segment === "wip") filters.taskIds = wipTaskIds;
  if (segment === "assignee") filters.assigneeId = assigneeId;
  return filters;
}

export function createCollectionOverviewController(capabilities: CollectionOverviewCapabilities) {
  function open(): void {
    const document = capabilities.document;
    const previous = document.activeElement as HTMLElement | null;
    const menuTrigger = previous?.closest("details.composer-menu")?.querySelector<HTMLElement>("summary");
    let drillingDown = false;
    const collectionId = capabilities.collectionId();
    const category = capabilities.categories().find(item => item.id === collectionId);
    const allTasks = capabilities.tasks();
    const scope = collectionId ? allTasks.filter(todo => todo.categoryId === collectionId) : allTasks;
    const members = [...capabilities.members()];
    const user = capabilities.user();
    if (user && !members.some(member => member.id === user.id)) members.unshift({ id: user.id, email: user.email, role: "owner" });
    const insights = collectionInsights(scope, category, [...new Set(members.map(member => member.id))], capabilities.today(), capabilities.now(), allTasks);
    const dialog = capabilities.document.createElement("dialog");
    dialog.className = "name-dialog overview-dialog";
    const label = overviewLabel(!!collectionId);
    dialog.setAttribute("aria-label", label);
    dialog.innerHTML = collectionInsightsView({ title: collectionId ? capabilities.collectionName(collectionId) : capabilities.rootName(), scopeLabel: label, insights, memberEmails: new Map(members.map(member => [member.id, member.email])), closeIcon: capabilities.closeIcon });
    const activate = async (segment: string, assigneeId = "") => {
      drillingDown = true;
      capabilities.activate(collectionOverviewFilters(capabilities.defaultFilters(), collectionId, segment, assigneeId, insights.wipTaskIds));
      dialog.close();
      await capabilities.render();
      document.querySelector<HTMLElement>("[data-task-filter-trigger]")?.focus({ preventScroll: true });
    };
    dialog.querySelector("[data-close]")!.addEventListener("click", () => dialog.close());
    dialog.querySelectorAll<HTMLElement>("[data-overview-segment]").forEach(button => button.addEventListener("click", () => void activate(button.dataset.overviewSegment || "open")));
    dialog.querySelectorAll<HTMLElement>("[data-overview-assignee]").forEach(button => button.addEventListener("click", () => void activate("assignee", button.dataset.overviewAssignee || "")));
    capabilities.document.body.append(dialog);
    dialog.addEventListener("close", () => {
      dialog.remove();
      if (drillingDown) return;
      const trigger = menuTrigger?.isConnected ? menuTrigger : previous?.isConnected && previous !== document.body ? previous : document.querySelector<HTMLElement>(".task-toolbar-more > summary");
      trigger?.focus({ preventScroll: true });
    });
    dialog.showModal();
  }

  return { open };
}
